import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, FileText } from 'lucide-react';
import { UserRequest, RequestStatus } from '../types';
import { ApiError, api } from '../services/api';
import { RequestCard } from './RequestCard';
import { useRequestPolling } from '../hooks/useRequestPolling';

interface RequestsSidebarProps {
  selectedRequestId?: string;
  onRequestSelect: (request: UserRequest) => void;
  newRequest?: UserRequest; // New prop for adding requests
  onNewRequestAdded?: () => void; // Callback to clear the newRequest state
  onPatientSelect?: (patientId: string, request: UserRequest) => void;
  updatedRequest?: UserRequest; // New prop for updating existing requests
  onAuthError?: (error: Error) => void;
}

export const RequestsSidebar: React.FC<RequestsSidebarProps> = ({
  selectedRequestId,
  onRequestSelect,
  newRequest,
  onNewRequestAdded,
  onPatientSelect,
  updatedRequest,
  onAuthError
}) => {
  const [requests, setRequests] = useState<UserRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const processedRequestIdsRef = useRef<Set<string>>(new Set());
  const onNewRequestAddedRef = useRef(onNewRequestAdded);

  // Polling callbacks
  const handleRequestUpdate = useCallback((updatedRequest: UserRequest) => {
    setRequests(prevRequests => 
      prevRequests.map(req => {
        if (req.id === updatedRequest.id) {
          // Preserve patient name if the update doesn't have it but we do
          return {
            ...updatedRequest,
            patientName: updatedRequest.patientName || req.patientName
          };
        }
        return req;
      })
    );
  }, []);

  const handleRequestComplete = useCallback((completedRequest: UserRequest) => {
    console.log('Request completed:', completedRequest.id);
    // The request is already updated in the list via handleRequestUpdate
  }, []);

  const handleRequestFailed = useCallback((failedRequest: UserRequest) => {
    console.log('Request failed:', failedRequest.id);
    // The request is already updated in the list via handleRequestUpdate
  }, []);

  // Initialize polling
  const { startPolling, stopPolling, stopAllPolling } = useRequestPolling({
    onRequestUpdate: handleRequestUpdate,
    onRequestComplete: handleRequestComplete,
    onRequestFailed: handleRequestFailed
  });

  // Keep callback ref up to date
  useEffect(() => {
    onNewRequestAddedRef.current = onNewRequestAdded;
  }, [onNewRequestAdded]);

  // Fetch requests on mount and start polling for active requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const userRequests = await api.getUserRequests();
        console.log(`RequestsSidebar: Loaded ${userRequests.length} requests`);
        console.log('Request patient names:', userRequests.map(r => r.patientName));
        setRequests(userRequests);
        
        // Start polling for requests that are still in progress and from the last 30 minutes
        const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
        userRequests.forEach(request => {
          const status = request.status;
          const isActive = status === RequestStatus.InProgress || 
                          status === RequestStatus.Pending || 
                          String(status).toLowerCase() === 'inprogress' || 
                          String(status).toLowerCase() === 'processing' || 
                          String(status).toLowerCase() === 'pending';
          
          const isRecent = new Date(request.createdAt) > thirtyMinutesAgo;
          
          if (isActive && isRecent) {
            console.log('Starting polling for recent active request:', request.id);
            startPolling(request);
          } else if (isActive && !isRecent) {
            console.log('Skipping polling for old active request:', request.id);
          }
        });
      } catch (err) {
        console.error('Error fetching requests:', err);
        if (err instanceof ApiError && err.status === 401) {
          onAuthError?.(err);
          setError('Please log in to load visits.');
        } else {
          setError('Failed to load requests');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();

    // Cleanup polling on unmount
    return () => {
      stopAllPolling();
    };
  }, [startPolling, stopAllPolling]);

  // Listen for patient updates to refresh list (e.g., name changes)
  useEffect(() => {
    const handler = async () => {
      try {
        const userRequests = await api.getUserRequests();
        setRequests(userRequests);
      } catch (e) {
        console.error('Failed to refresh requests on patient update', e);
        if (e instanceof ApiError && e.status === 401) {
          onAuthError?.(e);
          setError('Please log in to load visits.');
        }
      }
    };
    window.addEventListener('patientsUpdated', handler as EventListener);
    return () => window.removeEventListener('patientsUpdated', handler as EventListener);
  }, []);

  // Add new request when provided
  useEffect(() => {
    if (!newRequest) return;
    
    const requestId = newRequest.id;
    
    // Skip if we've already processed this request ID
    if (processedRequestIdsRef.current.has(requestId)) {
      return;
    }
    
    console.log('Adding new request to sidebar:', newRequest);
    
    setRequests(prevRequests => {
      // Check if request already exists in the list
      const exists = prevRequests.some(req => req.id === requestId);
      if (exists) {
        console.log('Request already exists in sidebar:', requestId);
        // Mark as processed even if it exists to prevent repeated checks
        processedRequestIdsRef.current.add(requestId);
        return prevRequests;
      }
      
      console.log('New request added to sidebar:', requestId);
      // Mark as processed
      processedRequestIdsRef.current.add(requestId);
      
      // Add new request at the top of the list
      const updatedRequests = [newRequest, ...prevRequests];
      
      // Start polling if the request is active and recent
      const status = newRequest.status;
      const isActive = status === RequestStatus.InProgress || 
                      status === RequestStatus.Pending || 
                      String(status).toLowerCase() === 'inprogress' || 
                      String(status).toLowerCase() === 'processing' || 
                      String(status).toLowerCase() === 'pending';
      
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
      const isRecent = new Date(newRequest.createdAt) > thirtyMinutesAgo;
      
      if (isActive && isRecent) {
        console.log('Starting polling for new request:', requestId);
        startPolling(newRequest);
      } else if (isActive && !isRecent) {
        console.log('New request is too old, not starting polling:', requestId);
      } else {
        console.log('Request not in active status, not starting polling:', newRequest.status);
      }
      
      // Notify parent that the request has been added (use ref to avoid dependency)
      if (onNewRequestAddedRef.current) {
        onNewRequestAddedRef.current();
      }
      
      return updatedRequests;
    });
  }, [newRequest?.id, startPolling]); // Only depend on the ID, not the whole object

  // Update existing request when provided
  useEffect(() => {
    if (updatedRequest) {
      console.log('Updating request in sidebar:', updatedRequest.id);
      setRequests(prevRequests => 
        prevRequests.map(req => {
          if (req.id === updatedRequest.id) {
            // Merge the updated request, preserving patient name if it exists
            return {
              ...updatedRequest,
              patientName: updatedRequest.patientName || req.patientName
            };
          }
          return req;
        })
      );
    }
  }, [updatedRequest]);

  // Handle request deletion
  const handleDeleteRequest = useCallback(async (requestId: string) => {
    try {
      await api.deleteUserRequest(requestId);
      // Stop polling for this specific request
      stopPolling(requestId);
      // Remove the request from the list
      setRequests(prevRequests => prevRequests.filter(req => req.id !== requestId));
      setDeleteError(null); // Clear any previous delete errors
      console.log('Request deleted:', requestId);
    } catch (err) {
      console.error('Error deleting request:', err);
      if (err instanceof ApiError && err.status === 401) {
        onAuthError?.(err);
        setDeleteError('Please log in to delete visits.');
      } else {
        // Show error message without replacing the entire sidebar
        setDeleteError('Failed to delete request. Please try again.');
      }
      // Clear error after 5 seconds
      setTimeout(() => setDeleteError(null), 5000);
    }
  }, [stopPolling]);

  // Filter requests based on search term
  const filteredRequests = requests.filter(request => {
    const patientName = request.patientName || '';
    const taskName = request.taskName || '';
    
    return patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           taskName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-base-100 border-r border-base-300">
        <div className="flex flex-col items-center justify-center p-8 text-base-content/70 gap-4">
          <div className="loading loading-spinner loading-lg"></div>
          <span>Loading visits...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col bg-base-100 border-r border-base-300">
        <div className="flex flex-col items-center justify-center p-8 text-error gap-4">
          <div className="w-8 h-8 rounded-full bg-error text-error-content flex items-center justify-center text-lg font-bold">!</div>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-base-100 border-r border-base-300">
      <div className="p-4 border-b border-base-300 bg-base-100">
        <h2 className="text-xl font-semibold text-base-content mb-4">Visits</h2>
        {deleteError && (
          <div className="alert alert-error mb-4">
            <span>{deleteError}</span>
          </div>
        )}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
          <input
            type="text"
            placeholder="Search visits by patient..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-base-200 border border-base-300 rounded-lg text-base-content placeholder-base-content/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-base-content/70 gap-4">
            <FileText size={24} />
            <span>No visits found</span>
          </div>
        ) : (
          <>
            {filteredRequests.map((request) => {
              return (
                <RequestCard
                  key={request.id}
                  request={request}
                  isSelected={selectedRequestId === request.id}
                  onClick={() => onRequestSelect(request)}
                  onPatientClick={
                    onPatientSelect
                      ? (patientId: string, clickedRequest: UserRequest) =>
                          onPatientSelect(patientId, clickedRequest)
                      : undefined
                  }
                  onDelete={handleDeleteRequest}
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}; 